import { dashboardSchemaTelemetryRowSchema } from "@arti/schemas/dashboard";

import type {
  ListSchemaTelemetryOptions,
  ListSchemaTelemetryResponse,
  SchemaTelemetryRecord,
} from "../types/schemaTelemetry";

import {
  type DashboardSchemaTelemetryRow,
  exportSchemaTelemetryAsUser,
  getTelemetryRowByIdAsUser,
  listSchemaTelemetryAsUser,
} from "../dashboard-client";
import { client } from "../dashboard-client/client.gen";

function normalizeRow(row: DashboardSchemaTelemetryRow): SchemaTelemetryRecord {
  return row;
}

function buildListQuery(options: ListSchemaTelemetryOptions): {
  page?: string;
  pageSize?: string;
  schemaName?: string;
  provider?: string;
  model?: string;
  callsite?: string;
  validationStatus?: string;
  hasParsedResult?: string;
  hasRawResponse?: string;
  search?: string;
  from?: string;
  to?: string;
  scopeType?: string;
  scopeId?: string;
} {
  return {
    callsite: options.callsite,
    from: options.from,
    hasParsedResult:
      options.hasParsedResult !== undefined
        ? String(options.hasParsedResult)
        : undefined,
    hasRawResponse:
      options.hasRawResponse !== undefined
        ? String(options.hasRawResponse)
        : undefined,
    model: options.model,
    page: options.page ? String(options.page) : undefined,
    pageSize: options.pageSize ? String(options.pageSize) : undefined,
    provider: options.provider,
    schemaName: options.schemaName,
    scopeId: options.scopeId,
    scopeType: options.scopeType,
    search: options.search,
    to: options.to,
    validationStatus: options.validationStatus,
  };
}

export async function fetchSchemaTelemetryList(
  options: ListSchemaTelemetryOptions = {},
): Promise<ListSchemaTelemetryResponse> {
  const { data } = await listSchemaTelemetryAsUser({
    query: buildListQuery(options),
  });
  return { ...data, rows: data.rows.map(normalizeRow) };
}

export async function fetchSchemaTelemetryDetail(
  id: number,
): Promise<SchemaTelemetryRecord> {
  const { data } = await getTelemetryRowByIdAsUser({ path: { id } });

  const parsed = dashboardSchemaTelemetryRowSchema.parse(data);
  return normalizeRow(parsed);
}

export function getSchemaTelemetryPromptUrl(id: number): string {
  return client.buildUrl({
    path: { id },
    url: "/user/schema-telemetry/{id}/prompt",
  });
}

export function getSchemaTelemetrySchemaUrl(id: number): string {
  return client.buildUrl({
    path: { id },
    url: "/user/schema-telemetry/{id}/schema",
  });
}

export async function exportSchemaTelemetry(ids: number[]): Promise<void> {
  const { data } = await exportSchemaTelemetryAsUser({
    body: { ids },
    parseAs: "blob",
  });

  // ParseAs: "blob" yields a Blob at runtime regardless of the schema type
  const blob = new Blob([data], { type: "application/json" });

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `schema-telemetry-export-${new Date().toISOString()}.json`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}
